import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';
import SimpleRichTextEditor from './SimpleRichTextEditor';

// ReactQuill has compatibility issues with React 19
// Using SimpleRichTextEditor as fallback

/**
 * BusinessTemplateEditor - Admin template editor for business pin offers
 * 
 * Features:
 * - Rich text editor for offer content (with emojis)
 * - Business name field
 * - Terms field (plain text)
 * - Creates/updates special offers
 */

const BusinessTemplateEditor = ({ park, existingOffer, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    businessName: '',
    offerContent: '',
    terms: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Using SimpleRichTextEditor (React 19 compatible)

  useEffect(() => {
    if (existingOffer) {
      setFormData({
        businessName: existingOffer.business_name || '',
        offerContent: existingOffer.offer_content || '',
        terms: existingOffer.terms || ''
      });
    }
  }, [existingOffer]);

  const handleSave = async () => {
    // Validation
    if (!formData.offerContent.trim()) {
      toast.error('Please enter offer details');
      return;
    }

    if (!formData.businessName.trim()) {
      toast.error('Please enter business name');
      return;
    }

    setIsSaving(true);

    try {
      const token = localStorage.getItem('token');
      
      console.log('🎯 Creating offer for park:', park);
      console.log('📍 Park ID:', park.id);
      console.log('📌 Park Category:', park.category);
      
      const offerData = {
        park_id: park.id,
        category: park.category || 'featured',
        // Store rich text HTML as offer content
        offer_title: formData.businessName,
        offer_description: formData.offerContent, // Rich HTML content
        offer_terms: formData.terms,
        terms: formData.terms, // Also store in new terms field
        expiration_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
        business_name: formData.businessName // Store business name separately
      };
      
      console.log('📤 Sending offer data:', offerData);

      let response;
      if (existingOffer) {
        // Update existing offer
        response = await axios.put(
          `${BACKEND_URL}/api/admin/special-offers/${existingOffer.id}`,
          offerData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success('Offer updated successfully!');
      } else {
        // Create new offer
        response = await axios.post(
          `${BACKEND_URL}/api/admin/special-offers`,
          offerData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        toast.success('Offer created successfully!');
      }

      if (onSave) {
        onSave(response.data);
      }

      onClose();

    } catch (error) {
      console.error('Error saving offer:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to save offers');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to save offer');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePin = async () => {
    setIsDeleting(true);

    try {
      const token = localStorage.getItem('token');

      // Step 1: Delete all offers associated with this park
      if (existingOffer) {
        await axios.delete(
          `${BACKEND_URL}/api/admin/special-offers/${existingOffer.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
      }

      // Step 2: Delete the park itself (admin endpoint for cascade delete)
      await axios.delete(
        `${BACKEND_URL}/api/admin/parks/${park.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      toast.success('Business pin deleted successfully!');
      
      // Close modal and trigger reload
      onClose();
      
      // Reload page to update map
      setTimeout(() => {
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error deleting pin:', error);
      
      if (error.response?.status === 401) {
        toast.error('Please log in to delete pins');
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this pin');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to delete pin');
      }
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {existingOffer ? 'Edit Business Offer' : 'Create Business Offer'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {park.name || 'Business Location'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSaving}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Offer Content (Rich Text) - MOVED TO TOP */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Special Offer Details * 
              <span className="text-gray-500 font-normal ml-2">(Use formatting toolbar or type HTML)</span>
            </label>
            <SimpleRichTextEditor
              value={formData.offerContent}
              onChange={(content) => setFormData({ ...formData, offerContent: content })}
              placeholder="Enter your special offer details here... Use HTML formatting and emojis! 🎉🐾"
            />
            <p className="mt-2 text-xs text-gray-500">
              💡 <strong>Tip:</strong> Type emojis directly using your keyboard emoji picker (Ctrl+Cmd+Space on Mac, Win+. on Windows)
            </p>
          </div>

          {/* Business Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Business Name *
            </label>
            <input
              type="text"
              value={formData.businessName}
              onChange={(e) => setFormData({ ...formData, businessName: e.target.value })}
              placeholder="e.g., Paws & Coffee"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              maxLength={200}
            />
            <p className="mt-1 text-xs text-gray-500">
              This name will be displayed to users viewing the offer
            </p>
          </div>

          {/* Terms & Conditions (Plain Text) - IMPROVED FORMATTING */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Terms & Conditions
              <span className="text-gray-500 font-normal ml-2">(Optional but recommended)</span>
            </label>
            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <textarea
                value={formData.terms}
                onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
                placeholder="Enter terms and conditions for this offer (e.g., Valid for new customers only, One per household, Expires in 30 days)"
                rows={5}
                className="w-full px-4 py-3 border-0 focus:ring-2 focus:ring-emerald-500 resize-none"
                maxLength={1000}
              />
              <div className="bg-gray-50 border-t border-gray-300 px-4 py-2 flex justify-between items-center">
                <p className="text-xs text-gray-600">
                  Users must accept these terms before receiving the offer
                </p>
                <span className="text-xs font-medium text-gray-500">{formData.terms.length}/1000</span>
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-emerald-800 mb-2">📧 Email Delivery</h3>
            <p className="text-sm text-emerald-700">
              When users request this offer, they'll receive a beautifully formatted email with:
            </p>
            <ul className="mt-2 text-sm text-emerald-600 space-y-1 ml-4">
              <li>• Your special offer details (as formatted above)</li>
              <li>• Business name and contact information</li>
              <li>• Terms & conditions (if provided)</li>
              <li>• Happy Paws branding in emerald green theme</li>
            </ul>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between items-center">
          {/* Delete Pin Button (Left side) */}
          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSaving || isDeleting}
            className="px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete Pin
          </button>

          {/* Right side actions */}
          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSaving || isDeleting}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || isDeleting || !formData.offerContent.trim() || !formData.businessName.trim()}
              className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {existingOffer ? 'Update Offer' : 'Create Offer'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Delete Business Pin?</h3>
                  <p className="text-sm text-gray-600 mt-1">This action cannot be undone.</p>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 font-medium mb-2">This will permanently delete:</p>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    The business pin location
                  </li>
                  {existingOffer && (
                    <li className="flex items-center gap-2">
                      <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                      The special offer ({existingOffer.business_name || 'Untitled'})
                    </li>
                  )}
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePin}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    'Delete Pin'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessTemplateEditor;
